import { create } from "zustand";

export interface ChatSource {
  id: number;
  sourceType: "summary" | "message";
  sourceId: number;
  metadata: any;
  content: string;
}

/**
 * Agent execution step for displaying tool activity in UI
 */
export interface AgentExecutionStep {
  type: "tool_call" | "tool_result" | "thinking" | "response";
  toolName?: string;
  toolDisplayName?: string;
  toolIcon?: string;
  args?: Record<string, unknown>;
  resultsCount?: number;
  error?: boolean;
  timestamp: number;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  role: "user" | "assistant" | "system" | "tool";
  content: string;
  toolCallId?: string;
  toolName?: string;
  toolArguments?: any;
  metadata?: {
    sources?: ChatSource[];
    toolCalls?: Array<{ name: string; args: any; result: string }>;
    pendingAction?: PendingAction;
  };
  createdAt: string;
}

/** A send or delete Que proposed; it runs only when the user confirms it. */
export interface PendingAction {
  tool: string;
  title: string;
  details: [string, string][];
  status: "pending" | "confirmed" | "cancelled";
  createdAt: string;
}

export interface ChatConversation {
  id: string;
  organizationId: string;
  userId: string;
  whatsappConversationId?: string | null;
  title: string | null;
  messageCount: number;
  lastMessageAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ChatFilters {
  dateRange?: { start: Date; end: Date };
  conversationIds?: string[];
  senderNames?: string[];
}

interface ChatState {
  // Modal state
  open: boolean;
  setOpen: (open: boolean) => void;

  // Advanced mode for filters
  advancedMode: boolean;
  setAdvancedMode: (mode: boolean) => void;

  // Filters
  filters: ChatFilters;
  setFilters: (filters: ChatFilters) => void;
  clearFilters: () => void;

  /** A question the inbox's Que panel sends as soon as its chat is ready, e.g. from the header menu */
  pendingPanelQuestion: string | null;
  setPendingPanelQuestion: (question: string | null) => void;

  // Current conversation
  currentConversation: ChatConversation | null;
  setCurrentConversation: (conversation: ChatConversation | null) => void;

  // Messages in current conversation
  messages: ChatMessage[];
  setMessages: (messages: ChatMessage[]) => void;
  addMessage: (message: ChatMessage) => void;

  // Conversations list
  conversations: ChatConversation[];
  setConversations: (conversations: ChatConversation[]) => void;

  // Input state
  input: string;
  setInput: (input: string) => void;

  // Loading state
  loading: boolean;
  streaming: boolean;
  error: string | null;

  // Reasoning state (shown during streaming for o-series models)
  currentReasoning: string;
  reasoningComplete: boolean;
  appendReasoning: (chunk: string) => void;
  setReasoningComplete: (complete: boolean) => void;
  clearReasoning: () => void;

  // Agent execution steps (shown during streaming)
  agentSteps: AgentExecutionStep[];
  setAgentSteps: (steps: AgentExecutionStep[]) => void;
  addAgentStep: (step: AgentExecutionStep) => void;
  clearAgentSteps: () => void;

  // Actions
  createConversation: (orgId: string, title?: string, whatsappConversationId?: string) => Promise<ChatConversation | null>;
  loadConversations: (orgId: string) => Promise<void>;
  loadConversation: (orgId: string, conversationId: string) => Promise<void>;
  sendMessage: (
    orgId: string,
    conversationId: string,
    message: string,
    stream?: boolean,
    confirm?: boolean
  ) => Promise<void>;
  deleteConversation: (orgId: string, conversationId: string) => Promise<void>;
  clearCurrentConversation: () => void;
}

export const useChatStore = create<ChatState>((set, get) => ({
  // Modal state
  open: false,
  setOpen: (open) => set({ open }),

  // Advanced mode
  advancedMode: false,
  setAdvancedMode: (mode) => set({ advancedMode: mode }),

  // Filters
  filters: {},
  setFilters: (filters) => set({ filters }),
  clearFilters: () => set({ filters: {} }),

  pendingPanelQuestion: null,
  setPendingPanelQuestion: (question) => set({ pendingPanelQuestion: question }),

  // Current conversation
  currentConversation: null,
  setCurrentConversation: (conversation) =>
    set({ currentConversation: conversation }),

  // Messages
  messages: [],
  setMessages: (messages) => set({ messages }),
  addMessage: (message) =>
    set((state) => ({ messages: [...state.messages, message] })),

  // Conversations list
  conversations: [],
  setConversations: (conversations) => set({ conversations }),

  // Input state
  input: "",
  setInput: (input) => set({ input }),

  // Loading state
  loading: false,
  streaming: false,
  error: null,

  // Reasoning state
  currentReasoning: "",
  reasoningComplete: false,
  appendReasoning: (chunk) =>
    set((state) => ({ currentReasoning: state.currentReasoning + chunk })),
  setReasoningComplete: (complete) => set({ reasoningComplete: complete }),
  clearReasoning: () => set({ currentReasoning: "", reasoningComplete: false }),

  // Agent execution steps
  agentSteps: [],
  setAgentSteps: (steps) => set({ agentSteps: steps }),
  addAgentStep: (step) =>
    set((state) => ({ agentSteps: [...state.agentSteps, step] })),
  clearAgentSteps: () => set({ agentSteps: [] }),

  // Actions
  createConversation: async (orgId: string, title?: string, whatsappConversationId?: string) => {
    set({ loading: true, error: null });

    try {
      const response = await fetch(`/chat/conversations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          title: title || "New Chat",
          whatsappConversationId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create conversation");
      }

      const data = await response.json();

      set({
        currentConversation: data.conversation,
        messages: [],
        loading: false,
        error: null,
      });

      // Reload conversations list
      get().loadConversations(orgId);

      return data.conversation;
    } catch (error) {
      console.error("Create conversation error:", error);
      set({
        loading: false,
        error: error instanceof Error ? error.message : "Failed to create conversation",
      });
      return null;
    }
  },

  loadConversations: async (orgId: string) => {
    try {
      const response = await fetch(`/chat/conversations`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to load conversations");
      }

      const data = await response.json();
      set({ conversations: data.conversations || [] });
    } catch (error) {
      console.error("Load conversations error:", error);
    }
  },

  loadConversation: async (orgId: string, conversationId: string) => {
    // Don't interrupt if we're currently streaming
    // This prevents race condition when navigating after createConversation + sendMessage
    const currentState = get();
    if (currentState.streaming) {
      // Check if we already have messages for this conversation (just created it)
      const hasMessagesForConversation = currentState.messages.some(
        (m) => m.conversationId === conversationId
      );
      if (hasMessagesForConversation) {
        console.log("[chatStore] Skipping loadConversation - already streaming with messages for this conversation");
        return;
      }
    }

    // Clear everything when switching conversations - messages, streaming state, agent steps
    set({
      loading: true,
      error: null,
      messages: [], // Clear old messages immediately
      streaming: false,
      agentSteps: [],
      currentReasoning: "",
      reasoningComplete: false,
    });

    try {
      const response = await fetch(`/chat/conversations/${conversationId}`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to load conversation");
      }

      const data = await response.json();

      set({
        currentConversation: data.conversation,
        messages: data.messages || [],
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error("Load conversation error:", error);
      set({
        loading: false,
        error: error instanceof Error ? error.message : "Failed to load conversation",
      });
    }
  },

  sendMessage: async (
    orgId: string,
    conversationId: string,
    message: string,
    stream = true,
    confirm?: boolean
  ) => {
    const userMessage: ChatMessage = {
      id: `temp_${Date.now()}`,
      conversationId,
      role: "user",
      content: message,
      createdAt: new Date().toISOString(),
    };

    // Add user message immediately and clear any previous agent steps and reasoning
    set((state) => ({
      messages: [...state.messages, userMessage],
      input: "",
      streaming: stream,
      error: null,
      agentSteps: [], // Clear steps for new message
      currentReasoning: "", // Clear reasoning for new message
      reasoningComplete: false,
    }));

    try {
      const currentFilters = get().filters;

      const response = await fetch(
        `/chat/conversations/${conversationId}/messages`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            message,
            stream,
            filters: currentFilters && Object.keys(currentFilters).length > 0 ? currentFilters : undefined,
            // Only the Confirm button runs a proposed action; a typed message cancels it
            confirm: confirm ?? false,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to send message");
      }

      if (stream) {
        // Handle SSE streaming response
        console.log("Starting streaming response...");
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          console.error("No response body for streaming");
          throw new Error("No response body");
        }

        let assistantContent = "";
        let sources: ChatSource[] | undefined = undefined;
        const assistantMessage: ChatMessage = {
          id: `temp_assistant_${Date.now()}`,
          conversationId,
          role: "assistant",
          content: "",
          createdAt: new Date().toISOString(),
        };

        // Add empty assistant message
        set((state) => ({
          messages: [...state.messages, assistantMessage],
        }));

        // Stream chunks in SSE format
        let buffer = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            console.log("Streaming complete");
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          // Parse SSE events
          const lines = buffer.split("\n");
          buffer = lines.pop() || ""; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const data = line.slice(6); // Remove "data: " prefix
              try {
                const parsed = JSON.parse(data);

                if (parsed.reasoning) {
                  // Reasoning summary event from o-series models
                  get().appendReasoning(parsed.reasoning);
                } else if (parsed.reasoningComplete) {
                  // Reasoning is complete
                  get().setReasoningComplete(true);
                } else if (parsed.step) {
                  // Agent execution step event
                  const step: AgentExecutionStep = {
                    type: parsed.step.type,
                    toolName: parsed.step.toolName,
                    toolDisplayName: parsed.step.toolDisplayName,
                    toolIcon: parsed.step.toolIcon,
                    args: parsed.step.args,
                    resultsCount: parsed.step.resultsCount,
                    error: parsed.step.error,
                    timestamp: parsed.step.timestamp || Date.now(),
                  };
                  get().addAgentStep(step);
                } else if (parsed.content) {
                  console.log("[chatStore] Received content chunk:", parsed.content.length, "chars");
                  assistantContent += parsed.content;

                  // Update assistant message
                  const targetId = assistantMessage.id;
                  console.log("[chatStore] Updating message with id:", targetId, "total content:", assistantContent.length);
                  set((state) => {
                    const foundMsg = state.messages.find(m => m.id === targetId);
                    console.log("[chatStore] Found message in state:", !!foundMsg);
                    return {
                      messages: state.messages.map((msg) =>
                        msg.id === targetId
                          ? { ...msg, content: assistantContent }
                          : msg
                      ),
                    };
                  });
                } else if (parsed.done) {
                  console.log("Stream done signal received");
                  if (parsed.pendingAction) {
                    set((state) => ({
                      messages: state.messages.map((msg) =>
                        msg.id === assistantMessage.id
                          ? { ...msg, metadata: { ...msg.metadata, pendingAction: parsed.pendingAction } }
                          : msg
                      ),
                    }));
                  }
                  // Capture sources if available
                  if (parsed.sources && Array.isArray(parsed.sources)) {
                    sources = parsed.sources;
                    // Update message with sources
                    set((state) => ({
                      messages: state.messages.map((msg) =>
                        msg.id === assistantMessage.id
                          ? {
                              ...msg,
                              metadata: {
                                ...msg.metadata,
                                sources,
                              },
                            }
                          : msg
                      ),
                    }));
                  }

                  // Clear agent steps and reasoning after completion
                  get().clearAgentSteps();
                  get().clearReasoning();

                  // Note: We don't reload conversation here to avoid flicker
                  // The temp messages work fine for the current session
                  // Real IDs will be loaded when user navigates back to this conversation
                } else if (parsed.error) {
                  console.error("Stream error:", parsed.error);
                  throw new Error(parsed.error);
                }
              } catch (e) {
                console.error("Failed to parse SSE data:", data, e);
              }
            }
          }
        }

        set({ streaming: false });
      } else {
        // Handle non-streaming response
        const data = await response.json();

        // Replace temp user message with saved message
        set((state) => ({
          messages: [
            ...state.messages.filter((msg) => msg.id !== userMessage.id),
            data.userMessage,
            data.assistantMessage,
          ],
          streaming: false,
        }));
      }

      // Update conversation's message count
      const { currentConversation } = get();
      if (currentConversation) {
        set({
          currentConversation: {
            ...currentConversation,
            messageCount: currentConversation.messageCount + 2,
            lastMessageAt: new Date().toISOString(),
          },
        });
      }
    } catch (error) {
      console.error("Send message error:", error);
      set({
        streaming: false,
        error: error instanceof Error ? error.message : "Failed to send message",
      });
    }
  },

  deleteConversation: async (orgId: string, conversationId: string) => {
    try {
      const response = await fetch(`/chat/conversations/${conversationId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to delete conversation");
      }

      // Remove from conversations list
      set((state) => ({
        conversations: state.conversations.filter((c) => c.id !== conversationId),
      }));

      // Clear current conversation if it's the one being deleted
      const { currentConversation } = get();
      if (currentConversation?.id === conversationId) {
        set({
          currentConversation: null,
          messages: [],
        });
      }
    } catch (error) {
      console.error("Delete conversation error:", error);
      set({
        error: error instanceof Error ? error.message : "Failed to delete conversation",
      });
    }
  },

  clearCurrentConversation: () =>
    set({
      currentConversation: null,
      messages: [],
      input: "",
      error: null,
    }),
}));
